/**
 * Beschriftetes Formularfeld.
 *
 * Übernommen aus dem Buchungsdialog und um `error` erweitert: Bei zwanzig
 * Feldern reicht eine Fehlerliste über dem Formular nicht mehr — man findet die
 * beanstandete Stelle sonst nicht wieder.
 */
export function Field({
  label,
  hint,
  optional,
  error,
  children,
}: {
  label: string;
  hint?: string;
  optional?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">
        {label}
        {optional && <span className="ml-1 font-normal text-slate-400">(optional)</span>}
      </span>
      {hint && <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>}
      <div className="mt-1.5">{children}</div>
      {error && (
        <span className="mt-1 block text-xs text-red-700" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

/** Einheitliche Feldoptik, damit nicht jede Maske ihre eigenen Klassen erfindet. */
export const inputClass =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500 disabled:bg-slate-50 disabled:text-slate-500';

/**
 * Datum und Uhrzeit nebeneinander.
 *
 * Die beiden Klassen sind der Grund für diesen Baustein: Ohne `min-w-0`
 * schrumpfen Flex-Kinder nicht unter ihre Eigenbreite, und ein Datumsfeld
 * bringt von Haus aus reichlich davon mit. In einer halbbreiten Spalte
 * schieben sich die Felder dann übereinander.
 */
export function DateTimeInput({
  date,
  time,
  onDateChange,
  onTimeChange,
  minDate,
  maxDate,
  stepSeconds,
  label,
}: {
  date: string;
  time: string;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  minDate?: string;
  maxDate?: string;
  stepSeconds: number;
  label: string;
}) {
  return (
    <div className="flex gap-2">
      {/*
        Die Breite steuern die Umhüllenden, nicht die Felder selbst.

        `inputClass` bringt `w-full` mit. Eine angehängte Klasse wie `w-24`
        gewinnt dagegen nicht verlässlich: Bei Tailwind entscheidet die
        Reihenfolge im erzeugten Stylesheet, nicht die im Klassenattribut. Zwei
        konkurrierende Breitenangaben am selben Element sind deshalb ein
        Glücksspiel — hier füllt jedes Feld schlicht seine Hülle.
      */}
      <div className="min-w-0 flex-1">
        <input
          type="date"
          value={date}
          min={minDate}
          max={maxDate}
          onChange={(event) => onDateChange(event.target.value)}
          className={inputClass}
          aria-label={`${label} — Datum`}
        />
      </div>
      <div className="w-32 shrink-0">
        <input
          type="time"
          step={stepSeconds}
          value={time}
          onChange={(event) => onTimeChange(event.target.value)}
          className={inputClass}
          aria-label={`${label} — Uhrzeit`}
        />
      </div>
    </div>
  );
}

export function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded-md border border-slate-200 p-4">
      <legend className="px-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {legend}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
